/**
 * Page Temps Non Facturables (TNF) — RH
 * Deux onglets : Saisie TNF | Types TNF
 * Conforme ARCHITECTURE.md
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit2, Clock, Tag, Search, ChevronLeft, ChevronRight, X, Check, Pencil,
} from 'lucide-react';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtH(h) {
  if (!h && h !== 0) return '—';
  const hh = Math.floor(h);
  const mm  = Math.round((h - hh) * 60);
  return `${hh}h${mm > 0 ? String(mm).padStart(2, '0') : ''}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// ─── Styles partagés ────────────────────────────────────────────────────────

const inp   = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
const inpSt = { borderColor: 'var(--border)' };
const lbl   = "block text-xs font-semibold uppercase tracking-wider mb-1";
const lblSt = { color: 'var(--text-muted)',  };

// ═══════════════════════════════════════════════════════════════
// Onglet Types TNF
// ═══════════════════════════════════════════════════════════════

function TypesTNFTab() {
  const [types, setTypes]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm]     = useState({ libelle: '', couleur: '#6B7280', actif: true });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getTypesTNF();
      setTypes(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Impossible de charger les types TNF'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ libelle: '', couleur: '#6B7280', actif: true });
    setEditId(null);
  };

  const startEdit = (t) => {
    setForm({ libelle: t.libelle || '', couleur: t.couleur || '#6B7280', actif: t.actif !== false });
    setEditId(t.type_tnf_id);
  };

  const handleSave = async () => {
    if (!form.libelle.trim()) { toast.error('Le libellé est obligatoire'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.updateTypeTNF(editId, form);
        toast.success('Type modifié');
      } else {
        await api.createTypeTNF(form);
        toast.success('Type créé');
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type TNF ?')) return;
    try {
      await api.deleteTypeTNF(id);
      toast.success('Type supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur (ce type est peut-être utilisé)');
    }
  };

  return (
    <div className="space-y-5">
      {/* Formulaire */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
        <p className={lbl} style={lblSt}>{editId ? 'Modifier le type' : 'Nouveau type TNF'}</p>
        <div className="flex gap-3 flex-wrap items-end">
          <div style={{ flex: '3 1 200px' }}>
            <label className={lbl} style={lblSt}>Libellé <span style={{ color: '#DC2626' }}>*</span></label>
            <input type="text" value={form.libelle}
              onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))}
              placeholder="Ex: Formation, Réunion, Entretien matériel..."
              maxLength={100} data-testid="type-tnf-libelle"
              className={inp} style={inpSt} />
          </div>
          <div style={{ flex: '0 0 80px' }}>
            <label className={lbl} style={lblSt}>Couleur</label>
            <input type="color" value={form.couleur}
              onChange={e => setForm(f => ({ ...f, couleur: e.target.value }))}
              data-testid="type-tnf-couleur"
              className="w-full h-9 rounded-lg border cursor-pointer"
              style={{ padding: 2, borderColor: 'var(--border)' }} />
          </div>
          <div style={{ flex: '1 1 100px' }}>
            <label className={lbl} style={lblSt}>Statut</label>
            <select value={form.actif ? 'actif' : 'inactif'}
              onChange={e => setForm(f => ({ ...f, actif: e.target.value === 'actif' }))}
              data-testid="type-tnf-actif"
              className={inp} style={{ ...inpSt, background: 'white' }}>
              <option value="actif">Actif</option>
              <option value="inactif">Inactif</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} data-testid="type-tnf-save"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving
                ? <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
                : <Plus size={14} />}
              {editId ? 'Enregistrer' : 'Créer'}
            </button>
            {editId && (
              <button onClick={resetForm}
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : types.length === 0 ? (
        <div className="rounded-xl py-12 text-center" style={{ border: '1px dashed var(--border)' }}>
          <Tag size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun type TNF défini</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)' }}>
                {['Libellé', 'Couleur', 'Statut', ''].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map((t, i) => (
                <tr key={t.type_tnf_id}
                  style={{ borderBottom: i < types.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: t.couleur || '#6B7280' }} />
                      <span className="font-medium" style={{ color: 'var(--text)' }}>{t.libelle}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{t.couleur}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{
                        background: t.actif !== false ? '#DCFCE7' : '#F3F4F6',
                        color:      t.actif !== false ? '#16A34A' : '#6B7280',
                      }}>
                      {t.actif !== false ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => startEdit(t)} data-testid={`edit-type-${t.type_tnf_id}`}
                        className="p-1.5 rounded hover:bg-gray-100"
                        style={{ color: 'var(--text-muted)' }}>
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDelete(t.type_tnf_id)} data-testid={`del-type-${t.type_tnf_id}`}
                        className="p-1.5 rounded hover:bg-red-50"
                        style={{ color: 'var(--text-disabled)' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Onglet Saisie TNF
// ═══════════════════════════════════════════════════════════════

function SaisieTNFTab() {
  const { activeAgences } = useSociete();
  const [employes,  setEmployes]  = useState([]);
  const [typesTNF,  setTypesTNF]  = useState([]);
  const [tnfList,   setTnfList]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [dateDebut, setDateDebut] = useState(firstOfMonth());
  const [dateFin,   setDateFin]   = useState(todayIso());
  const [filterEmp, setFilterEmp] = useState('');
  const [form, setForm] = useState({
    employe_id: '', type_tnf_id: '', date: todayIso(), date_fin: todayIso(), duree_reelle: '', note: '',
  });
  const [saving, setSaving] = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const startEdit = (t) => {
    setEditingId(t.tnf_id);
    setEditForm({
      type_tnf_id:    t.type_tnf_id    || '',
      date:           t.date           || '',
      duree_reelle: t.duree_reelle != null ? String(t.duree_reelle) : '',
      note:           t.note           || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const handleEdit = async () => {
    const h = parseFloat(editForm.duree_reelle);
    if (!editForm.type_tnf_id) { toast.error('Type TNF obligatoire'); return; }
    if (!editForm.date)         { toast.error('Date obligatoire'); return; }
    if (isNaN(h) || h <= 0)     { toast.error('Durée invalide'); return; }
    setEditSaving(true);
    try {
      await api.updateTNF(editingId, { ...editForm, duree_reelle: h });
      toast.success('TNF modifié');
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setEditSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empR, typeR, tnfR] = await Promise.all([
        api.getEmployes({ statut: 'actif', limit: 200 }),
        api.getTypesTNF(),
        api.getTNF({ date_debut: dateDebut, date_fin: dateFin, employe_id: filterEmp || undefined }),
      ]);
      setEmployes(Array.isArray(empR.data) ? empR.data : (empR.data?.items || []));
      setTypesTNF(Array.isArray(typeR.data) ? typeR.data.filter(t => t.actif !== false) : []);
      setTnfList(Array.isArray(tnfR.data) ? tnfR.data : []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [dateDebut, dateFin, filterEmp, activeAgences]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.employe_id)  { toast.error('Sélectionnez un employé'); return; }
    if (!form.type_tnf_id) { toast.error('Sélectionnez un type TNF'); return; }
    if (!form.date)        { toast.error('Date début obligatoire'); return; }
    const h = parseFloat(form.duree_reelle);
    if (!form.duree_reelle || isNaN(h) || h <= 0) { toast.error('Durée invalide (ex: 2.5)'); return; }
    const dfin = form.date_fin && form.date_fin >= form.date ? form.date_fin : form.date;
    setSaving(true);
    try {
      const res = await api.createTNF({ ...form, date_fin: dfin, duree_reelle: h });
      const nb = res.data?.created ?? 1;
      toast.success(nb > 1 ? `${nb} TNF ajoutés` : 'TNF ajouté');
      setForm(f => ({ ...f, type_tnf_id: '', duree_reelle: '', note: '', date_fin: f.date }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce TNF ?')) return;
    try {
      await api.deleteTNF(id);
      await load();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const getEmpNom = (emp) => {
    if (!emp) return '';
    if (emp.personne) return `${emp.personne.prenom || ''} ${emp.personne.nom || ''}`.trim();
    return emp.employe_id;
  };

  const totalH = tnfList.reduce((s, t) => s + (t.duree_reelle || 0), 0);

  return (
    <div className="space-y-5">
      {/* Formulaire */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
        <p className={lbl} style={lblSt}>Saisir un temps non facturable</p>
        <div className="flex gap-3 flex-wrap items-end">

          <div style={{ flex: '2 1 160px' }}>
            <label className={lbl} style={lblSt}>Employé <span style={{ color: '#DC2626' }}>*</span></label>
            <select value={form.employe_id}
              onChange={e => setForm(f => ({ ...f, employe_id: e.target.value }))}
              data-testid="tnf-employe"
              className={inp} style={{ ...inpSt, background: 'white' }}>
              <option value="">— Sélectionner —</option>
              {employes.map(e => (
                <option key={e.employe_id} value={e.employe_id}>{getEmpNom(e)}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '2 1 160px' }}>
            <label className={lbl} style={lblSt}>Type TNF <span style={{ color: '#DC2626' }}>*</span></label>
            <select value={form.type_tnf_id}
              onChange={e => setForm(f => ({ ...f, type_tnf_id: e.target.value }))}
              data-testid="tnf-type"
              className={inp} style={{ ...inpSt, background: 'white' }}>
              <option value="">— Sélectionner —</option>
              {typesTNF.map(t => (
                <option key={t.type_tnf_id} value={t.type_tnf_id}>{t.libelle}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 120px' }}>
            <label className={lbl} style={lblSt}>Date début <span style={{ color: '#DC2626' }}>*</span></label>
            <input type="date" value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value, date_fin: f.date_fin < e.target.value ? e.target.value : f.date_fin }))}
              data-testid="tnf-date"
              className={inp} style={inpSt} />
          </div>

          <div style={{ flex: '1 1 120px' }}>
            <label className={lbl} style={lblSt}>Date fin</label>
            <input type="date" value={form.date_fin} min={form.date}
              onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))}
              data-testid="tnf-date-fin"
              className={inp} style={inpSt} />
          </div>

          <div style={{ flex: '1 1 90px' }}>
            <label className={lbl} style={lblSt}>Durée (h) <span style={{ color: '#DC2626' }}>*</span></label>
            <input type="number" value={form.duree_reelle}
              onChange={e => setForm(f => ({ ...f, duree_reelle: e.target.value }))}
              placeholder="2.5" min="0.25" max="24" step="0.25"
              data-testid="tnf-duree"
              className={inp} style={inpSt} />
          </div>

          <div style={{ flex: '2 1 150px' }}>
            <label className={lbl} style={lblSt}>Note</label>
            <input type="text" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Optionnel..." maxLength={300}
              data-testid="tnf-note"
              className={inp} style={inpSt} />
          </div>

          <button onClick={handleAdd} disabled={saving} data-testid="tnf-add-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--primary-gradient)', flexShrink: 0 }}>
            {saving
              ? <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
              : <Plus size={14} />}
            Ajouter
          </button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : tnfList.length === 0 ? (
        <div className="rounded-xl py-12 text-center" style={{ border: '1px dashed var(--border)' }}>
          <Clock size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun TNF sur la période</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)' }}>
                {['Employé', 'Type', 'Date', 'Durée', 'Note', ''].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tnfList.map((t, i) => {
                const typeInfo = typesTNF.find(x => x.type_tnf_id === t.type_tnf_id);
                const couleur  = typeInfo?.couleur || '#6B7280';
                const isEditing = editingId === t.tnf_id;
                return (
                  <tr key={t.tnf_id}
                    style={{ borderBottom: i < tnfList.length - 1 ? '1px solid var(--border-light)' : 'none',
                             background: isEditing ? 'var(--bg-subtle)' : 'transparent' }}>
                    {/* Employé — non modifiable ici */}
                    <td className="px-4 py-2.5 font-medium" style={{ color: 'var(--text)' }}>
                      {employes.find(e => e.employe_id === t.employe_id)
                        ? getEmpNom(employes.find(e => e.employe_id === t.employe_id))
                        : t.employe_id}
                    </td>
                    {/* Type */}
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select value={editForm.type_tnf_id}
                          onChange={e => setEditForm(f => ({ ...f, type_tnf_id: e.target.value }))}
                          className={inp} style={{ ...inpSt, background: 'white', minWidth: 140 }}>
                          <option value="">—</option>
                          {typesTNF.map(tp => (
                            <option key={tp.type_tnf_id} value={tp.type_tnf_id}>{tp.libelle}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: couleur }} />
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {t.type_tnf_libelle || t.type_tnf_id}
                          </span>
                        </div>
                      )}
                    </td>
                    {/* Date */}
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input type="date" value={editForm.date}
                          onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                          className={inp} style={{ ...inpSt, width: 140 }} />
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{fmtDate(t.date)}</span>
                      )}
                    </td>
                    {/* Durée */}
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input type="number" value={editForm.duree_reelle}
                          onChange={e => setEditForm(f => ({ ...f, duree_reelle: e.target.value }))}
                          min="0.25" max="24" step="0.25"
                          className={inp} style={{ ...inpSt, width: 90 }} />
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                          {fmtH(t.duree_reelle)}
                        </span>
                      )}
                    </td>
                    {/* Note */}
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input type="text" value={editForm.note}
                          onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                          placeholder="Note..." maxLength={300}
                          className={inp} style={{ ...inpSt, minWidth: 160 }} />
                      ) : (
                        <span className="text-xs max-w-48 truncate block" style={{ color: 'var(--text-muted)' }}
                          title={t.note}>
                          {t.note || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                        </span>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-2 text-right">
                      {isEditing ? (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={handleEdit} disabled={editSaving}
                            data-testid={`save-tnf-${t.tnf_id}`}
                            className="p-1.5 rounded hover:bg-green-50 transition-colors disabled:opacity-50"
                            style={{ color: '#16A34A' }}>
                            {editSaving
                              ? <div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" />
                              : <Check size={14} />}
                          </button>
                          <button onClick={cancelEdit}
                            data-testid={`cancel-tnf-${t.tnf_id}`}
                            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                            style={{ color: 'var(--text-muted)' }}>
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => startEdit(t)}
                            data-testid={`edit-tnf-${t.tnf_id}`}
                            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                            style={{ color: 'var(--text-muted)' }}>
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => handleDelete(t.tnf_id)}
                            data-testid={`del-tnf-${t.tnf_id}`}
                            className="p-1.5 rounded hover:bg-red-50 transition-colors"
                            style={{ color: 'var(--text-disabled)' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Page principale
// ═══════════════════════════════════════════════════════════════

export default function TempsNonFacturables() {
  const [tab, setTab] = useState('saisie');

  const TABS = [
    { key: 'saisie', label: 'Saisie TNF', icon: Clock },
    { key: 'types',  label: 'Types TNF',  icon: Tag  },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
          Temps non facturables
        </h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Gérez les temps de travail hors réparations facturable
        </p>
      </div>

      {/* Onglets */}
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px"
            style={{
              borderBottomColor: tab === t.key ? 'var(--primary-solid)' : 'transparent',
              color: tab === t.key ? 'var(--primary-solid)' : 'var(--text-muted)',
              background: 'transparent',
            }}>
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === 'saisie' && <SaisieTNFTab />}
      {tab === 'types'  && <TypesTNFTab />}
    </div>
  );
}
